import { useNotice } from '../ui/notifications';
import { useEffect, useState } from 'react';
import { ACCENT_COLORS } from '@scribbleplot/shared';
import { color, font } from '../tokens';
import { Avatar, Button, Eyebrow, TextInput, Toast, Toggle } from '../ui/primitives';
import { initialsOf, useSession } from '../lib/session';
import { ApiError, api } from '../lib/api';
import type { Navigate } from '../lib/router';
import { SecuritySection } from './SecuritySection';
import { DangerZone } from './DangerZone';

/** Profile, appearance, and account. */

export function SettingsScreen({ accent, navigate }: { accent: string; navigate: Navigate }) {
  const user = useSession((s) => s.user);
  const updateName = useSession((s) => s.updateName);
  const updatePrefs = useSession((s) => s.updatePrefs);
  const signOut = useSession((s) => s.signOut);

  const [name, setName] = useState(user?.name ?? '');
  const [boardCount, setBoardCount] = useState<number | null>(null);
  const { notice: toast, notify: setToast } = useNotice();
  const [savingName, setSavingName] = useState(false);

  useEffect(() => setName(user?.name ?? ''), [user?.name]);

  const [resending, setResending] = useState(false);

  /** Report the outcome of following a confirmation link. */
  useEffect(() => {
    const outcome = new URLSearchParams(location.search).get('verify');
    if (!outcome) return;
    history.replaceState(null, '', location.pathname);
    if (outcome === 'ok') {
      setToast('Email confirmed');
      // The session that followed the link is the authoritative one now.
      void useSession.getState().bootstrap();
    } else {
      setToast('That confirmation link has expired — send a new one.');
    }
  }, []);

  useEffect(() => {
    api
      .listBoards()
      .then(({ boards }) => setBoardCount(boards.length))
      .catch(() => undefined);
  }, []);

  const prefs = user?.prefs ?? {};
  const dotGrid = prefs.dotGrid ?? true;
  const showMinimap = prefs.showMinimap ?? true;
  const presence = prefs.presence ?? true;

  const savePref = async (patch: Parameters<typeof updatePrefs>[0]) => {
    try {
      await updatePrefs(patch);
    } catch (err) {
      setToast({ kind: 'error', message: err instanceof ApiError ? err.message : 'Could not save that.' });
    }
  };

  const commitName = async () => {
    if (savingName) return;
    const trimmed = name.trim();
    if (!trimmed || trimmed === user?.name) {
      setName(user?.name ?? '');
      return;
    }
    setSavingName(true);
    try {
      await updateName(trimmed);
      setToast('Name updated');
    } catch (err) {
      setToast({ kind: 'error', message: err instanceof ApiError ? err.message : 'Could not save your name.' });
    } finally {
      setSavingName(false);
    }
  };

  const row = {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    marginTop: 16,
  } as const;

  return (
    <div style={{ height: '100%', display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
      <header
        style={{
          height: 56,
          display: 'flex',
          alignItems: 'center',
          gap: 10,
          padding: '0 22px',
          borderBottom: `1px solid ${color.line}`,
          flex: 'none',
        }}
      >
        <Button
          title="Back to boards"
          ariaLabel="Back to boards"
          onClick={() => navigate({ name: 'home' })}
          hoverStyle={{ background: color.hoverStrong }}
          style={{
            width: 32,
            height: 32,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            borderRadius: 9,
            border: 'none',
            background: 'transparent',
            color: color.icon,
          }}
        >
          <svg width={16} height={16} viewBox="0 0 16 16" aria-hidden>
            <path
              d="M9.5 3.5 5 8l4.5 4.5"
              style={{
                fill: 'none',
                stroke: 'currentColor',
                strokeWidth: 1.6,
                strokeLinecap: 'round',
                strokeLinejoin: 'round',
              }}
            />
          </svg>
        </Button>
        <div style={{ fontSize: 15, fontWeight: 700 }}>Settings</div>
      </header>

      <div style={{ flex: 1, overflowY: 'auto' }}>
        <div
          style={{
            maxWidth: 560,
            margin: '0 auto',
            padding: '42px 28px 80px',
            display: 'flex',
            flexDirection: 'column',
            gap: 36,
          }}
        >
          {user && !user.emailVerified && (
            <div
              role="status"
              style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                gap: 12,
                padding: '12px 14px',
                border: `1px solid ${color.lineStrong}`,
                borderRadius: 12,
                background: color.input,
              }}
            >
              <div>
                <div style={{ fontSize: 13, fontWeight: 600 }}>Confirm your email address</div>
                <div style={{ fontSize: 12, color: color.textDim, marginTop: 2, lineHeight: 1.5 }}>
                  Your boards work as normal. Confirming lets you invite people to them by email.
                </div>
              </div>
              <Button
                onClick={async () => {
                  if (resending) return;
                  setResending(true);
                  try {
                    const { alreadyVerified } = await api.resendVerification();
                    setToast(alreadyVerified ? 'Already confirmed' : 'Confirmation sent');
                    if (alreadyVerified) void useSession.getState().bootstrap();
                  } catch (err) {
                    setToast({
                      kind: 'error',
                      message: err instanceof ApiError ? err.message : 'Could not send that.',
                    });
                  } finally {
                    setResending(false);
                  }
                }}
                hoverStyle={{ background: color.hover }}
                style={{
                  height: 30,
                  padding: '0 13px',
                  borderRadius: 9,
                  border: `1px solid ${color.lineStrong}`,
                  background: 'transparent',
                  color: color.text,
                  fontSize: 12.5,
                  fontWeight: 600,
                  flex: 'none',
                }}
              >
                {resending ? 'Sending…' : 'Resend'}
              </Button>
            </div>
          )}

          <section>
            <Eyebrow>Profile</Eyebrow>
            <div style={{ display: 'flex', alignItems: 'center', gap: 16, marginTop: 14 }}>
              <Avatar initials={initialsOf(user?.name)} accent={accent} size={54} />
              <div style={{ flex: 1, display: 'flex', flexDirection: 'column', gap: 8 }}>
                <TextInput
                  value={name}
                  onChange={setName}
                  ariaLabel="Display name"
                  onKeyDown={(event) => {
                    if (event.key === 'Enter') (event.target as HTMLInputElement).blur();
                  }}
                />
                <div style={{ fontSize: 12, color: color.textDim }}>{user?.email}</div>
              </div>
            </div>
            <div style={{ marginTop: 10 }}>
              <Button
                disabled={savingName}
                loading={savingName}
                onClick={() => void commitName()}
                hoverStyle={{ background: color.hover }}
                style={{
                  height: 30,
                  padding: '0 13px',
                  borderRadius: 9,
                  border: `1px solid ${color.lineStrong}`,
                  background: 'transparent',
                  color: color.text,
                  fontSize: 12.5,
                  fontWeight: 600,
                }}
              >
                {savingName ? 'Saving…' : 'Save name'}
              </Button>
            </div>
          </section>

          <section>
            <Eyebrow>Appearance</Eyebrow>

            <div style={row}>
              <div style={{ fontSize: 13.5, fontWeight: 600 }}>Accent color</div>
              <div style={{ display: 'flex', gap: 9 }}>
                {ACCENT_COLORS.map((swatch) => (
                  <button
                    key={swatch}
                    type="button"
                    aria-label={`Accent ${swatch}`}
                    aria-pressed={accent === swatch}
                    onClick={() => void savePref({ accent: swatch })}
                    style={{
                      width: 26,
                      height: 26,
                      borderRadius: '50%',
                      background: swatch,
                      cursor: 'pointer',
                      border: accent === swatch ? '2px solid #fff' : '2px solid rgba(255,255,255,.15)',
                      transition: 'transform .1s',
                      padding: 0,
                    }}
                  />
                ))}
              </div>
            </div>

            <div style={row}>
              <div>
                <div style={{ fontSize: 13.5, fontWeight: 600 }}>Dot grid</div>
                <div style={{ fontSize: 12, color: color.textDim, marginTop: 2 }}>
                  Show the alignment grid on the canvas.
                </div>
              </div>
              <Toggle on={dotGrid} onChange={(on) => void savePref({ dotGrid: on })} accent={accent} label="Dot grid" />
            </div>

            <div style={row}>
              <div>
                <div style={{ fontSize: 13.5, fontWeight: 600 }}>Minimap</div>
                <div style={{ fontSize: 12, color: color.textDim, marginTop: 2 }}>
                  Overview map in the corner of the board.
                </div>
              </div>
              <Toggle
                on={showMinimap}
                onChange={(on) => void savePref({ showMinimap: on })}
                accent={accent}
                label="Minimap"
              />
            </div>

            <div style={row}>
              <div>
                <div style={{ fontSize: 13.5, fontWeight: 600 }}>Live cursors</div>
                <div style={{ fontSize: 12, color: color.textDim, marginTop: 2 }}>
                  Show collaborators’ pointers, and share yours.
                </div>
              </div>
              <Toggle
                on={presence}
                onChange={(on) => void savePref({ presence: on })}
                accent={accent}
                label="Live cursors"
              />
            </div>
          </section>

          <SecuritySection
            accent={accent}
            onNotify={setToast}
            onSignedOut={() => {
              // The session is already gone server-side; clear it locally too.
              useSession.setState({ user: null });
              navigate({ name: 'signin' }, { replace: true });
            }}
          />

          <section style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start' }}>
            <Eyebrow>Account &amp; plan</Eyebrow>
            <div
              style={{
                border: `1px solid ${color.lineSoft}`,
                borderRadius: 14,
                padding: 16,
                marginTop: 14,
                display: 'flex',
                alignItems: 'center',
                gap: 12,
                width: '100%',
              }}
            >
              <div style={{ flex: 1 }}>
                <div style={{ fontSize: 14, fontWeight: 700 }}>Free plan</div>
                <div style={{ fontSize: 12, color: color.textDim, marginTop: 3 }}>
                  {boardCount === null
                    ? 'Up to 5,000 items per board'
                    : `${boardCount} board${boardCount === 1 ? '' : 's'} · up to 5,000 items per board`}
                </div>
              </div>
            </div>

            <Button
              onClick={async () => {
                await signOut();
                navigate({ name: 'landing' }, { replace: true });
              }}
              hoverStyle={{ background: 'rgba(248,113,113,.08)' }}
              style={{
                marginTop: 18,
                height: 34,
                padding: '0 16px',
                borderRadius: 9,
                border: `1px solid rgba(248,113,113,.35)`,
                background: 'transparent',
                color: color.danger,
                fontSize: 12.5,
                fontWeight: 600,
              }}
            >
              Sign out
            </Button>
          </section>

          <DangerZone
            onNotify={setToast}
            onGone={() => {
              useSession.setState({ user: null });
              navigate({ name: 'landing' }, { replace: true });
            }}
          />

          <div style={{ fontSize: 11, fontFamily: font.mono, color: color.textGhost }}>scribbleplot.com — beta</div>
        </div>
      </div>

      <Toast message={toast} />
    </div>
  );
}
